import _ from "lodash";
import {
  ControlLabel,
  Divider,
  Form,
  FormControl,
  FormGroup,
  Icon,
  IconButton,
  Loader,
  Panel,
  AutoComplete,
} from "rsuite";
import PropTypes from "prop-types";
import React, { useEffect, useState, useContext } from "react";
import { PriceInfoData } from "../common/MIPriceInfo";
import { createUseStyles } from "react-jss";
import TravellerForm from "../common/TravellerForm";
import PaymentForm from "../common/PaymentForm";
import Fuse from "fuse.js";

import FailedBooking from "../common/FailedBooking";
import FailedPayment from "../common/FailedPayment";
import SuccessfulPayment from "../common/SuccesfulPayment";
import { TranslationContext } from "../../umbilical_tools";
import { withRouter } from "react-router-dom/cjs/react-router-dom.min";
import { BookingProcessHydrator } from "../../umbilical_hocs";
import jssVariables from "../../jss_setup";
import SuccessfulBooking from "../common/succesful_booking";

const Actions = (props) => {
  const {
    step,
    onPrevStep,
    onGoToRemarks,
    onGoToFinalBooking,
    initPayment,
    history,
  } = props;
  const { t } = useContext(TranslationContext);

  return (
    <React.Fragment>
      {[4, 6, 7, 8].includes(step) ? null : (
        <IconButton
          icon={<Icon icon="angle-left" />}
          appearance="ghost"
          onClick={onPrevStep}>
          <strong>{t("back")}</strong>
        </IconButton>
      )}
      {step === 1 ? (
        <IconButton
          icon={<Icon icon="angle-right" />}
          appearance="primary"
          onClick={onGoToRemarks}
          placement="right">
          <strong>{t("nextStep")}</strong>
        </IconButton>
      ) : step === 2 ? (
        <IconButton
          icon={<Icon icon="angle-right" />}
          appearance="primary"
          onClick={onGoToFinalBooking}
          placement="right">
          <strong>{t("book")}</strong>
        </IconButton>
      ) : step === 3 ? (
        <IconButton
          icon={<Icon icon="angle-right" />}
          appearance="primary"
          onClick={initPayment}
          placement="right">
          <strong>{t("checkout")}</strong>
        </IconButton>
      ) : [7, 8].includes(step) ? (
        <IconButton
          icon={<Icon icon="home" />}
          appearance="primary"
          onClick={function () {
            history.push("/");
          }}
          placement="right">
          <strong>{t("backToHome")}</strong>
        </IconButton>
      ) : null}
    </React.Fragment>
  );
};
Actions.propTypes = {
  step: PropTypes.number.isRequired,
  onPrevStep: PropTypes.func.isRequired,
  onGoToRemarks: PropTypes.func.isRequired,
  onGoToFinalBooking: PropTypes.func.isRequired,
  initPayment: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
};

const travellersFormsStyles = createUseStyles({
  forms: {
    display: "grid",
    gridGap: jssVariables.normal_gap,
    overflow: "auto",
    height: "100%",
    gridAutoFlow: "row",
    gridAutoRows: "max-content",
  },
  [`@media ${jssVariables.media.smallscreen}`]: {
    forms: {
      gridAutoFlow: "unset",
      gridTemplateColumns: (props) =>
        props.pax <= 2 ? "1fr" : "repeat(2, 1fr)",
    },
  },
});
export const TravellersForms = (props) => {
  const { baseColor, guests, onTravellerFormChange } = props;

  const pax =
    _.get(guests, "adults", []).length + _.get(guests, "children", []).length;

  const classes = travellersFormsStyles({ pax });

  return (
    <div className={classes.forms}>
      {_.get(guests, "adults", []).map((adult, idx) => (
        <TravellerForm
          baseColor={baseColor}
          key={idx}
          idx={idx + 1}
          type="adult"
          onChange={onTravellerFormChange}
          traveller={adult}
        />
      ))}
      {_.get(guests, "children", []).map((child, idx) => (
        <TravellerForm
          baseColor={baseColor}
          key={idx}
          idx={idx + 1}
          type="child"
          onChange={onTravellerFormChange}
          traveller={child}
        />
      ))}
    </div>
  );
};
TravellersForms.defaultProps = {
  baseColor: jssVariables.colors.base_color,
};
TravellersForms.propTypes = {
  baseColor: PropTypes.string.isRequired,
  guests: PropTypes.object.isRequired,
  onTravellerFormChange: PropTypes.func.isRequired,
};

const remarksStyles = createUseStyles({
  remarks: {
    display: "grid",
    gridGap: jssVariables.normal_gap,
    "& textarea": {
      minWidth: "unset",
      width: "100%",
    },
  },
  description: {
    fontSize: "large",
  },
  autocomplete: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gridGap: jssVariables.normal_gap,
    alignItems: "center",
  },
  autocompleteLabel: {
    gridColumn: "span 2",
  },
  autocompleteInput: {
    gridColumn: (props) => (props.loading ? "span 2" : "span 1"),
  },
});
const Remarks = (props) => {
  const {
    remarks,
    pickup,
    address,
    autocompletedPois,
    onChangeRemarks,
    onAutocompletePOI,
    onAutocompleteReset,
  } = props;

  const [poi, setPoi] = useState(pickup.name);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (loading && query) {
      onAutocompleteReset();
      onAutocompletePOI(query);
    }
  }, [loading]);

  useEffect(() => {
    setLoading(false);
  }, [autocompletedPois.map((p) => p.uid).join("")]);

  const classes = remarksStyles({ loading });
  const { t } = useContext(TranslationContext);

  return (
    <div className={classes.remarks}>
      <p className={classes.description}>{t("specifyPickUpPoint")}.</p>
      <Form
        fluid
        formValue={{ remarks, address, pickup, poi }}
        onChange={async (value, event) => {
          event.persist();
          const strCreator = (poi) => `${poi.name}, ${poi.location.join(", ")}`;

          const poiQuery = _.get(value, "poi", "");
          if (event.type === "change") {
            const poiAlreadyFound = autocompletedPois
              .map((poi) => strCreator(poi))
              .includes(poiQuery);

            if (poiQuery.length > 3 && !poiAlreadyFound) {
              setQuery(poiQuery);
              setLoading(true);
            }

            if (poiAlreadyFound) {
              var pickup = autocompletedPois.find((poi) => {
                return poiQuery === strCreator(poi);
              });

              if (pickup) {
                pickup = { name: strCreator(pickup) };
                value.pickup = pickup;
              }
            }
          }

          setPoi(poiQuery);
          onChangeRemarks(_.omit(value, "poi"));
        }}>
        <FormGroup className={classes.autocomplete}>
          <ControlLabel className={classes.autocompleteLabel}>
            <strong>{t("pickupPoint")}</strong>
          </ControlLabel>
          <FormControl
            className={classes.autocompleteInput}
            autoComplete="off"
            accepter={AutoComplete}
            name="poi"
            data={autocompletedPois.map((poi) => ({
              label: `${poi.name}, ${poi.location.join(", ")}`,
              value: `${poi.name}, ${poi.location.join(", ")}`,
            }))}
            filterBy={function (value) {
              const options = {
                shouldSort: true,
                keys: ["name"],
              };

              const fuse = new Fuse(autocompletedPois, options);

              return fuse.search(value).map((poi) => ({
                label: `${poi.item.name}, ${poi.item.location.join(", ")}`,
                value: `${poi.item.name}, ${poi.item.location.join(", ")}`,
              }));
            }}
          />
          {loading ? <Loader /> : null}
        </FormGroup>
        <FormGroup>
          <ControlLabel>
            <strong>{`${t("address")} (${t("optional")})`}</strong>
          </ControlLabel>
          <FormControl rows={5} name="address" componentClass="textarea" />
        </FormGroup>
        <Divider />
        <p className={classes.description}>
          {t("provideImportantInfoForYourTravel")}.
        </p>
        <FormGroup>
          <ControlLabel>
            <strong>{`${t("remarks")} (${t("optional")})`}</strong>
          </ControlLabel>
          <FormControl rows={10} name="remarks" componentClass="textarea" />
        </FormGroup>
      </Form>
    </div>
  );
};
Remarks.propTypes = {
  remarks: PropTypes.string.isRequired,
  address: PropTypes.string.isRequired,
  pickup: PropTypes.object.isRequired,
  autocompletedPois: PropTypes.array.isRequired,
  onChangeRemarks: PropTypes.func.isRequired,
  onAutocompletePOI: PropTypes.func.isRequired,
  onAutocompleteReset: PropTypes.func.isRequired,
};

const bookingProcessStyles = createUseStyles({
  MIBookingProcess: {
    position: "fixed",
    transform: "translateY(-50%)",
    background: (props) => props.background,
    top: "50%",
    left: "10%",
    width: "80%",
    zIndex: "9998",
    height: "75%",
    overflow: "hidden",
    "& .rs-panel-body": {
      overflow: "hidden",
      display: "grid",
      gridTemplateRows: "max-content max-content 1fr max-content",
      height: "100%",
    },
  },
  divider: {
    margin: `${jssVariables.normal_gap} 0 `,
  },
  content: { overflow: "auto" },
  actions: {
    display: "grid",
    gridTemplateColumns: (props) =>
      [5, 7, 8].includes(props.step) ? "1fr" : "repeat(2, 1fr)",
    gridGap: jssVariables.normal_gap,
    alignSelf: "end",
    marginTop: `calc(${jssVariables.normal_gap} * 2)`,
  },
  [`@media ${jssVariables.media.smallscreen}`]: {
    process: {
      width: "30%",
      left: "35%",
    },
  },
});
const MIBookingProcess = (props) => {
  const {
    baseColor,
    background,
    loading,
    paymentIntent,
    setIdle,
    setLoading,
    savePayment,
    paymentFailed,
    miPaymentSuccess,
    adults,
    children,
    date,
    price_info,
    details,
    step,
    guests,
    pickup,
    address,
    autocompletedPois,
    onGoToRemarks,
    onPrevStep,
    onGoToFinalBooking,
    initPayment,
    history,
    remarks,
    paymentResult,
    bookingResult,
    onChangeRemarks,
    onTravellerFormChange,
    onAutocompletePOI,
    onAutocompleteReset,
  } = props;

  const group_uid = price_info.price[0].group_uid;

  const classes = bookingProcessStyles({ step, background });
  const { t } = useContext(TranslationContext);

  return (
    <Panel className={classes.MIBookingProcess} shaded>
      <h4>
        {step === 1
          ? t("travellerInformation")
          : step === 2
          ? t("bookingRemarks")
          : step === 3
          ? t("bookingOverview")
          : step === 4
          ? t("checkout")
          : step === 5
          ? t("paymentFailed")
          : step === 6
          ? t("paymentSuccess")
          : ""}
      </h4>
      <Divider className={classes.divider} />
      <div className={classes.content}>
        {step === 1 ? (
          <TravellersForms
            baseColor={baseColor}
            guests={guests}
            onTravellerFormChange={onTravellerFormChange}
          />
        ) : step === 2 ? (
          <Remarks
            remarks={remarks}
            pickup={pickup}
            address={address}
            autocompletedPois={autocompletedPois}
            onChangeRemarks={onChangeRemarks}
            onAutocompletePOI={onAutocompletePOI}
            onAutocompleteReset={onAutocompleteReset}
          />
        ) : step === 3 ? (
          <PriceInfoData
            baseColor={props.baseColor}
            title={_.get(details, "title", "")}
            group_uid={group_uid}
            adults={adults}
            child={children}
            date={date}
            price_info={price_info}
            remarks={remarks}
            pickup={pickup}
            address={address}
          />
        ) : step === 4 ? (
          <PaymentForm
            loading={loading}
            price={_.get(price_info, "price.0.value", 0)}
            currency={_.get(price_info, "price.0.currency", "")}
            paymentIntent={paymentIntent}
            onBack={onPrevStep}
            setIdle={setIdle}
            setLoading={setLoading}
            savePayment={savePayment}
            paymentFailed={paymentFailed}
            paymentSuccess={miPaymentSuccess}
          />
        ) : step === 5 ? (
          <FailedPayment paymentResult={paymentResult} />
        ) : step === 6 ? (
          <SuccessfulPayment />
        ) : step === 7 ? (
          <FailedBooking />
        ) : step === 8 ? (
          <SuccessfulBooking bookingResult={bookingResult} />
        ) : null}
      </div>
      <div className={classes.actions}>
        <Actions
          step={step}
          onPrevStep={onPrevStep}
          onGoToRemarks={onGoToRemarks}
          onGoToFinalBooking={onGoToFinalBooking}
          initPayment={initPayment}
          history={history}
        />
      </div>
    </Panel>
  );
};
MIBookingProcess.defaultProps = {
  address: "",
  pickup: { name: "" },
  remarks: "",
};
MIBookingProcess.propTypes = {
  history: PropTypes.object.isRequired,
  adults: PropTypes.number.isRequired,
  children: PropTypes.number.isRequired,
  date: PropTypes.object.isRequired,
  price_info: PropTypes.object.isRequired,
  details: PropTypes.object.isRequired,
  baseColor: PropTypes.string.isRequired,
  background: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  step: PropTypes.number.isRequired,
  guests: PropTypes.object.isRequired,
  remarks: PropTypes.string.isRequired,
  address: PropTypes.string.isRequired,
  pickup: PropTypes.object.isRequired,
  paymentIntent: PropTypes.object.isRequired,
  paymentResult: PropTypes.object.isRequired,
  bookingResult: PropTypes.object.isRequired,
  autocompletedPois: PropTypes.array.isRequired,
  onPrevStep: PropTypes.func.isRequired,
  onTravellerFormChange: PropTypes.func.isRequired,
  onGoToRemarks: PropTypes.func.isRequired,
  onGoToFinalBooking: PropTypes.func.isRequired,
  onChangeRemarks: PropTypes.func.isRequired,
  initPayment: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  setIdle: PropTypes.func.isRequired,
  savePayment: PropTypes.func.isRequired,
  paymentFailed: PropTypes.func.isRequired,
  miPaymentSuccess: PropTypes.func.isRequired,
  onAutocompletePOI: PropTypes.func.isRequired,
  onAutocompleteReset: PropTypes.func.isRequired,
};

export default withRouter(BookingProcessHydrator(MIBookingProcess));
